import React from 'react';
import { useTranslation } from 'react-i18next';
import ThermometerIcon from '../../../_icons/ThermometerIcon';
import LightingIcon from '../../../_icons/LightingIcon';
import DoorenterIcon from '../../../_icons/DoorenterIcon';
import DisplayControlIcon from '../../../_icons/DisplayControlIcon';
import PowerIcon from '../../../_icons/PowerIcon';
import DumbbellIcon from '../../../_icons/DumbbellIcon';
import EtcIcon from '../../../_icons/EtcIcon';
import { CATEGORY_CARDS, cardOf, enabledChannels, unassignedChannels, enabledChannelsOfCard, presentCards, summarize } from './iotChannels';
import DoorIcon from '../../../_icons/DoorIcon';
import TimeForwardIcon from '../../../_icons/TimeForwardIcon';
import {
  LeftPanel,
  ZoneSection,
  ZoneTitleRow,
  ZoneTitle,
  RoomCard,
  RoomCardHeader,
  RoomName,
  RoomNameText,
  RoomStatusBadge,
  RoomTileRow,
  IoTControlItem,
  IoTControlLeft,
  IoTControlText,
  IoTControlRight,
  RoomAddButton,
} from './FacilityControlStyled';

const CARD_ICON = { LIGHT: LightingIcon, CLIMATE: ThermometerIcon, DISPLAY: DisplayControlIcon, EQUIPMENT: DumbbellIcon, ETC: EtcIcon };
const DOOR_STATUS_TEXT = { pending: '개방 중...', opened: '개방됨' };

/**
 * 좌측 룸 목록 — 룸별 가로 스크롤 타일: 일시개방 / 카테고리 카드… / 전원(미배정) / [카테고리 추가]. 매니저앱 LessonRoomList 와 같은 규칙
 * @param {Object} statusByRoom - {roomId: channels}
 * @param {Object} relaysByRoom - {roomId: relays[]}
 * @param {Object} extraCardsByRoom - {roomId: ['LIGHT']} 채널이 아직 없어도 보여줄 카드
 * @param {Object} sensorByRoom - {roomId: {online, alwaysOpen, openUntil}}
 * @param {Object} doorStatus - {roomId: 'pending'|'opened'}
 */
const ServiceZonePanel = ({
  rooms,
  selectedRoom,
  onSelectRoom,
  statusByRoom = {},
  relaysByRoom = {},
  extraCardsByRoom = {},
  sensorByRoom = {},
  doorStatus = {},
  onOpenDoor,
  onOpenCard,
  onAddCard,
}) => {
  const { t } = useTranslation();

  const groupedRooms = rooms.reduce((acc, room) => {
    const floorKey = room.floorNm || `${room.floorSeq}층`;
    if (!acc[floorKey]) acc[floorKey] = [];
    acc[floorKey].push(room);
    return acc;
  }, {});

  const isSelected = room => selectedRoom?.id === room.id;
  const iconColor = active => (active ? '#0F7F85' : '#999');

  const renderRoomCard = room => {
    const hasSensor = !!room.sensorId;
    const channels = statusByRoom[room.id];
    const loaded = hasSensor && !!channels;
    const relays = relaysByRoom[room.id];
    const sensor = sensorByRoom[room.id] || {};
    const disabledText = hasSensor ? (loaded ? null : t('상태 확인 중')) : t('센서 없음');
    const cardKeys = [...new Set([...presentCards(relays), ...(extraCardsByRoom[room.id] || [])])];
    const canAdd = cardKeys.length < CATEGORY_CARDS.length;
    const powerSum = summarize(channels, enabledChannels(relays));
    const powerEmpty = unassignedChannels(relays).length === 0;
    const doorPending = doorStatus[room.id] === 'pending';

    // 타일 클릭은 룸 선택을 겸한다 (우측 상세가 같은 룸으로 맞춰지도록)
    const tileClick = (e, action) => {
      e.stopPropagation();
      onSelectRoom(room);
      action?.();
    };

    const statusOf = (sum, busyText) =>
      disabledText || (sum.total === 0 ? t('전원 없음') : sum.onCount > 0 ? `${sum.onCount}/${sum.total} ${t('켜짐')}` : t('꺼짐'));

    // 타일은 기존 IoTControlItem 디자인(왼쪽 아이콘+라벨, 오른쪽 아이콘). 상태는 라벨 아래 작은 글씨
    const tile = ({ key, active, disabled, label, title, Icon, RightIcon, onClick }) => (
      <IoTControlItem key={key} isActive={active} isEnabled={!disabled} title={title} onClick={e => !disabled && tileClick(e, onClick)}>
        <IoTControlLeft>
          <Icon width={24} height={24} color={iconColor(active && !disabled)} />
          <IoTControlText isActive={active} isEnabled={!disabled}>
            {label}
          </IoTControlText>
        </IoTControlLeft>
        <IoTControlRight>
          <RightIcon width={26} height={26} color={iconColor(active && !disabled)} />
        </IoTControlRight>
      </IoTControlItem>
    );

    const powerTile = tile({
      key: 'POWER',
      active: powerSum.onCount > 0,
      disabled: !loaded,
      label: t('전원'),
      title: disabledText || (powerEmpty ? t('전원 없음') : statusOf(powerSum)),
      Icon: PowerIcon,
      RightIcon: TimeForwardIcon,
      onClick: () => onOpenCard(room, 'POWER'),
    });

    return (
      <RoomCard key={room.id} isSelected={isSelected(room)} isEnabled onClick={() => onSelectRoom(room)}>
        <RoomCardHeader>
          <RoomName>
            <RoomNameText isEnabled>{room.roomNm || t('룸')}</RoomNameText>
            {hasSensor ? (
              <RoomStatusBadge status={sensor.online ? 'active' : 'inactive'}>{sensor.online ? t('연결됨') : t('연결 끊김')}</RoomStatusBadge>
            ) : (
              <RoomStatusBadge status="inactive">{t('센서 없음')}</RoomStatusBadge>
            )}
          </RoomName>
          {/* 카테고리 추가는 룸 카드 오른쪽 상단 */}
          {canAdd && (
            <RoomAddButton disabled={!loaded} onClick={e => tileClick(e, () => onAddCard(room, cardKeys))}>
              ＋ {t('카테고리 추가')}
            </RoomAddButton>
          )}
        </RoomCardHeader>
        <RoomTileRow>
          {/* 일시개방: 누를 때마다 여는 버튼 (oneshot). 항시열림이면 누를 필요 없음 */}
          {tile({
            key: 'DOOR',
            active: hasSensor,
            disabled: !hasSensor || sensor.alwaysOpen || doorPending,
            // 진행 중/개방됨은 라벨로, 항시열림이면 라벨을 항시열림으로
            label: DOOR_STATUS_TEXT[doorStatus[room.id]] || (sensor.alwaysOpen ? t('항시열림') : t('일시개방')),
            title: !hasSensor
              ? t('센서 없음')
              : sensor.alwaysOpen
                ? sensor.openUntil
                  ? `${sensor.openUntil}${t('까지 열림')}`
                  : t('열려 있음')
                : t('탭하여 개방'),
            Icon: DoorenterIcon,
            RightIcon: DoorIcon,
            onClick: () => onOpenDoor(room),
          })}

          {cardKeys.length === 0 && powerTile}

          {cardKeys.map(key => {
            const card = cardOf(key);
            const sum = summarize(channels, enabledChannelsOfCard(relays, key));
            return tile({
              key,
              active: sum.onCount > 0,
              disabled: !loaded,
              label: t(card.label),
              title: statusOf(sum),
              Icon: CARD_ICON[key] || EtcIcon,
              RightIcon: TimeForwardIcon,
              onClick: () => onOpenCard(room, key),
            });
          })}

          {cardKeys.length > 0 && powerTile}
        </RoomTileRow>
      </RoomCard>
    );
  };

  return (
    <LeftPanel>
      {Object.entries(groupedRooms).map(([floorKey, floorRooms]) => (
        <ZoneSection key={floorKey}>
          <ZoneTitleRow>
            <ZoneTitle>{floorKey}</ZoneTitle>
          </ZoneTitleRow>
          {floorRooms.map(renderRoomCard)}
        </ZoneSection>
      ))}
      {rooms.length === 0 && (
        <ZoneSection>
          <ZoneTitleRow>
            <ZoneTitle>{t('등록된 룸이 없습니다')}</ZoneTitle>
          </ZoneTitleRow>
        </ZoneSection>
      )}
    </LeftPanel>
  );
};

export default ServiceZonePanel;
